import { gql } from 'apollo-boost';
import { client } from '@/lib/api';
import { preventError } from '@/utils/';
import State from '@/store/state';
import {
  GrantUserRoleInput,
  GrantUserRoleResponse,
  RevokeUserRoleInput,
  RevokeUserRoleResponse,
  PagerResponse,
  User,
  RevokeUserRoleMutation,
  RevokeUserRoleMutationVariables,
  GrantUserRoleMutation,
  GrantUserRoleMutationVariables,
  SignupUserMutation,
  SignupUserMutationVariables,
  ReInviteUserMutation,
  ReInviteUserMutationVariables,
  ResetUserPasswordMutation,
  ResetUserPasswordMutationVariables,
  UpdateUserMutation,
  UpdateUserMutationVariables,
  GetUserQuery,
  GetUserQueryVariables,
  GetUsersListQuery,
  GetUsersListQueryVariables,
  EnableUsersMutation,
  EnableUsersMutationVariables,
  DisableUsersMutation,
  DisableUsersMutationVariables,
  DeleteUsersMutation,
  DeleteUsersMutationVariables,
} from '@/generated/graphql';
import { useToast } from 'vue-toastification';
import { Module } from 'vuex';
import i18n from '@/i18n';
const toast = useToast();
const { t } = i18n.global;

type UsersState = State['users'];

export default {
  namespaced: true,

  // -----------------------------------------
  // STATE
  // -----------------------------------------
  state: (): UsersState => ({
    users: [],
    userPageInfo: {},
    activeSubUsers: 0,
  }),

  // -----------------------------------------
  // GETTERS
  // -----------------------------------------
  getters: {
    usersList(state): any[] {
      if (!state.users.length) return [];

      return state.users.map((user) => ({
        id: user.id,
        name: user.name || user.email,
        email: user.email,
        passwordResetInit: user.passwordResetInit,
        department: user.departments?.map((d) => d.name).join(',') || '',
        notActive: !user.active,
        dateAdded: user.createdAt,
        roles: user.roles?.map((r) => r.roleName).join(',') || '',
      }));
    },

    userPageInfo(state): PagerResponse {
      return state.userPageInfo;
    },
  },

  // -----------------------------------------
  // MUTATIONS
  // -----------------------------------------
  mutations: {
    SET_USER_LIST(state: UsersState, payload: User[]) {
      state.users = payload;
    },

    SET_USER_PAGEINFO(state: UsersState, payload: PagerResponse) {
      state.userPageInfo = payload;
    },

    SET_SUB_ACTIVE_USERS(state: UsersState, payload: number) {
      state.activeSubUsers = payload;
    },
  },

  // -----------------------------------------
  // ACTIONS
  // -----------------------------------------
  actions: {
    async DELETE_USERS(_, payload: { userIds: string[] }) {
      return client.mutate<DeleteUsersMutation, DeleteUsersMutationVariables>({
        mutation: gql`
          mutation deleteUsers($input: DeleteUsersInput!) {
            deleteUsers(input: $input) {
              status
            }
          }
        `,
        variables: {
          input: {
            ids: payload.userIds,
          },
        },
      });
    },
    async DISABLE_USERS(_, payload: { userIds: string[]; approvalId?: string }) {
      try {
        const response = await client.mutate<DisableUsersMutation, DisableUsersMutationVariables>({
          mutation: gql`
            mutation disableUsers($input: DisableUsersInput!) {
              disableUsers(input: $input) {
                status
                approvalRequest {
                  id
                  status
                }
              }
            }
          `,
          variables: {
            input: {
              ids: payload.userIds,
              ...(payload.approvalId ? { approvalId: payload.approvalId } : {}),
            },
          },
        });

        if (response.data.disableUsers.approvalRequest) {
          toast.info(t('approvalRequest.toasts.created'));
        } else {
          toast.success('User(s) disabled successfully');
        }

        return response.data.disableUsers;
      } catch (e: any) {
        preventError(e);
        throw e;
      }
    },
    async ENABLE_USERS(_, payload: { userIds: string[]; approvalId?: string }) {
      try {
        const response = await client.mutate<EnableUsersMutation, EnableUsersMutationVariables>({
          mutation: gql`
            mutation enableUsers($input: EnableUsersInput!) {
              enableUsers(input: $input) {
                status
                approvalRequest {
                  id
                  status
                }
              }
            }
          `,
          variables: {
            input: {
              ids: payload.userIds,
              ...(payload.approvalId ? { approvalId: payload.approvalId } : {}),
            },
          },
        });

        if (response.data.enableUsers.approvalRequest) {
          toast.info(t('approvalRequest.toasts.created'));
        } else {
          toast.success('User(s) enabled successfully');
        }

        return response.data.enableUsers;
      } catch (e: any) {
        preventError(e);
        throw e;
      }
    },
    // ================================
    // GET USERS LIST
    // ================================
    async GET_USERS({ commit, rootGetters }, payload) {
      const { pager, search } = payload;

      const input: any = {
        departmentId: payload.departmentId || [],
        roleIds: payload.roleIds || [], // ← тут, НЕ roleNames
        subscription: payload.subscription ?? null, // ← передаємо сабску
        institutionId: rootGetters.activeInstitutionId,
        pager,
        search,
      };

      try {
        const response = await client.query<GetUsersListQuery, GetUsersListQueryVariables>({
          query: gql`
            query GetUsersList($input: GetUsersInput!) {
              getUsers(input: $input) {
                activeCount
                items {
                  id
                  active
                  disabledSubByAdmin
                  name
                  email
                  passwordResetInit
                  createdAt
                  departments {
                    name
                  }
                  roles {
                    userRoleId
                    roleName
                    departmentName
                    institutionName
                  }
                  institution {
                    name
                  }
                }
                pager {
                  count
                  offset
                  limit
                }
              }
            }
          `,
          variables: { input },
        });

        commit('SET_USER_LIST', response.data.getUsers.items);
        commit('SET_USER_PAGEINFO', response.data.getUsers.pager);
        commit('SET_SUB_ACTIVE_USERS', response.data.getUsers.activeCount);
      } catch (e: any) {
        preventError(e);
      }
    },

    // ================================
    // GET ONE USER
    // ================================
    async GET_USER(_, payload) {
      const input = { id: payload.userId };

      try {
        const response = await client.query<GetUserQuery, GetUserQueryVariables>({
          query: gql`
            query GetUser($input: GetUsersInput!) {
              getUsers(input: $input) {
                items {
                  id
                  name
                  dateOfBirth
                  gender
                  email
                  avatarUrl
                  institution {
                    name
                  }
                  departments {
                    name
                  }
                }
              }
            }
          `,
          variables: { input },
        });

        const items = response.data.getUsers.items;
        if (!items.length) {
          toast.warning('USER_NOT_FOUND');
          return {};
        }

        return items[0];
      } catch (e: any) {
        preventError(e);
      }
    },

    // ================================
    // UPDATE USER
    // ================================
    async UPDATE_USER(_, payload) {
      const input = {
        id: payload.id,
        name: payload.name,
        dateOfBirth: payload.dateOfBirth,
        gender: payload.gender,
      };

      try {
        const response = await client.mutate<UpdateUserMutation, UpdateUserMutationVariables>({
          mutation: gql`
            mutation updateUser($input: UpdateUserInput!) {
              updateUser(input: $input) {
                user {
                  id
                  name
                  dateOfBirth
                  gender
                  email
                  avatarUrl
                  institution {
                    name
                  }
                  departments {
                    name
                  }
                }
              }
            }
          `,
          variables: { input },
        });

        if (response.errors?.length) {
          throw new Error(response.errors[0].message);
        }

        return response.data.updateUser.user;
      } catch (e: any) {
        preventError(e);
      }
    },

    // ================================
    // RESET PASSWORD
    // ================================
    async SEND_RESET_USER_PASSWORD(_, payload: { userId: string }) {
      const input = { id: payload.userId };

      try {
        const response = await client.mutate<ResetUserPasswordMutation, ResetUserPasswordMutationVariables>({
          mutation: gql`
            mutation resetUserPassword($input: ResetUserPasswordInput!) {
              resetUserPassword(input: $input) {
                status
              }
            }
          `,
          variables: { input },
        });

        return response.data.resetUserPassword.status;
      } catch (e: any) {
        preventError(e);
      }
    },

    // ================================
    // INVITE USER
    // ================================
    async INVITE_USERS({ rootGetters }, payload) {
      const input = {
        departmentId: payload.department,
        institutionId: rootGetters.activeInstitutionId,
        roleId: payload.roleId,
        email: payload.email,
      };

      try {
        const response = await client.mutate({
          mutation: gql`
            mutation inviteUsers($input: InviteUserInput!) {
              inviteUser(input: $input) {
                status
                message
              }
            }
          `,
          variables: { input },
        });

        toast.info(response.data.inviteUser.message);
        return response.data.inviteUser;
      } catch (e: any) {
        preventError(e);
        throw e;
      }
    },

    // ================================
    // REINVITE USER
    // ================================
    async REINVITE_USERS(_, payload: { id: string[] }) {
      const input = { userId: payload.id };

      try {
        const response = await client.mutate<ReInviteUserMutation, ReInviteUserMutationVariables>({
          mutation: gql`
            mutation reInviteUser($input: ReInviteUserInput!) {
              reInviteUser(input: $input) {
                status
                message
              }
            }
          `,
          variables: { input },
        });

        toast.info(response.data.reInviteUser.message);
        return response.data.reInviteUser;
      } catch (e: any) {
        preventError(e);
        throw e;
      }
    },

    // ================================
    // SIGNUP USER
    // ================================
    async SIGNUP_USER(_, payload: SignupUserMutationVariables['input']) {
      try {
        const response = await client.mutate<SignupUserMutation, SignupUserMutationVariables>({
          mutation: gql`
            mutation signupUser($input: SignupUserInput!) {
              signupUser(input: $input) {
                profile {
                  id
                  name
                }
              }
            }
          `,
          variables: { input: payload },
          errorPolicy: 'all',
        });

        return response.data.signupUser.profile;
      } catch (e: any) {
        preventError(e);
      }
    },

    // ================================
    // PERMISSIONS GRANT
    // ================================
    async GRANT_PERMISSIONS(_, payload: GrantUserRoleInput) {
      try {
        const response = await client.mutate<GrantUserRoleMutation, GrantUserRoleMutationVariables>({
          mutation: gql`
            mutation GrantUserRole($input: GrantUserRoleInput!) {
              grantUserRole(input: $input) {
                role {
                  id
                  roleName: role {
                    name
                  }
                }
                approvalRequest {
                  id
                  status
                }
              }
            }
          `,
          variables: { input: payload },
          errorPolicy: 'all',
        });

        if (response.data.grantUserRole.approvalRequest) {
          toast.info(t('approvalRequest.toasts.created'));
        } else {
          toast.success('Role granted successfully');
        }
      } catch (e: any) {
        preventError(e);
        throw e;
      }
    },

    async REVOKE_PERMISSIONS(_, payload: RevokeUserRoleInput) {
      try {
        const response = await client.mutate<RevokeUserRoleMutation, RevokeUserRoleMutationVariables>({
          mutation: gql`
            mutation RevokeUserRole($input: RevokeUserRoleInput!) {
              revokeUserRole(input: $input) {
                status
                approvalRequest {
                  id
                  status
                }
              }
            }
          `,
          variables: { input: payload },
          errorPolicy: 'all',
        });

        if (response.data.revokeUserRole.approvalRequest) {
          toast.info(t('approvalRequest.toasts.created'));
        } else {
          toast.success('Role revoked successfully');
        }

        return response.data.revokeUserRole.status;
      } catch (e: any) {
        preventError(e);
        throw e;
      }
    },
  },
} satisfies Module<UsersState, State>;
